<template>
  <el-dialog
    :title="title"
    :visible="visible"
    width="520px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <el-tabs v-if="showUrl" v-model="activeTab">
      <el-tab-pane label="本地上传" name="upload">
        <div class="image-source-upload">
          <el-upload
            ref="imageUpload"
            drag
            :headers="headers"
            :multiple="multiple"
            :limit="uploadLimit"
            :show-file-list="false"
            :before-upload="validateUploadFile"
            :on-exceed="handleExceed"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :http-request="directUpload ? handleDirectUpload : undefined"
            accept="image/*"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将图片拖到这里，或<em>点击上传</em></div>
          </el-upload>
        </div>
      </el-tab-pane>
      <el-tab-pane label="图片URL" name="url">
        <el-input
          v-model="urlText"
          type="textarea"
          :rows="multiple ? 5 : 3"
          :placeholder="multiple ? '每行一个图片URL' : '粘贴图片URL'"
        ></el-input>
        <div class="url-tips">支持 http 或 https 图片地址。</div>
      </el-tab-pane>
    </el-tabs>
    <div v-else class="image-source-upload">
      <el-upload
        ref="imageUpload"
        drag
        :headers="headers"
        :multiple="multiple"
        :limit="uploadLimit"
        :show-file-list="false"
        :before-upload="validateUploadFile"
        :on-exceed="handleExceed"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :http-request="directUpload ? handleDirectUpload : undefined"
        accept="image/*"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将图片拖到这里，或<em>点击上传</em></div>
      </el-upload>
    </div>
    <template #footer>
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" v-if="showUrl && activeTab === 'url'" @click="confirmUrl">确定</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { uploadDirectOss } from '@/assets/js/utils/ossDirectUpload'

export default {
  name: 'ImageSourceDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '添加图片',
    },
    headers: {
      type: Object,
      default: () => ({}),
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    existingUrls: {
      type: Array,
      default: () => [],
    },
    showUrl: {
      type: Boolean,
      default: false,
    },
    directUpload: {
      type: Boolean,
      default: false,
    },
    maxCount: {
      type: Number,
      default: 0,
    },
    aspectRatio: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeTab: 'upload',
      urlText: '',
      uploadSession: 0,
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.uploadSession += 1
        this.activeTab = 'upload'
        this.urlText = ''
        this.$nextTick(() => this.clearUploadFiles())
      } else {
        this.uploadSession += 1
        this.clearUploadFiles()
      }
    },
  },
  computed: {
    normalizedMaxCount() {
      const value = Number(this.maxCount)
      return Number.isInteger(value) && value > 0 ? value : 0
    },
    remainingUploadCount() {
      return this.normalizedMaxCount ? Math.max(this.normalizedMaxCount - this.existingUrls.length, 0) : 0
    },
    uploadLimit() {
      return this.normalizedMaxCount ? this.remainingUploadCount : undefined
    },
    ratioParts() {
      const match = String(this.aspectRatio || '').match(/^(\d+):(\d+)$/)
      if (!match || Number(match[1]) <= 0 || Number(match[2]) <= 0) return null
      return [Number(match[1]), Number(match[2])]
    },
  },
  methods: {
    handleExceed() {
      if (!this.normalizedMaxCount) return
      this.$message.warning(`最多上传${this.normalizedMaxCount}张图片，当前还可上传${this.remainingUploadCount}张`)
    },
    getImageSize(file) {
      return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => {
          URL.revokeObjectURL(objectUrl)
          resolve({ width: image.naturalWidth, height: image.naturalHeight })
        }
        image.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          reject(new Error('无法读取图片尺寸'))
        }
        image.src = objectUrl
      })
    },
    validateUploadFile(file) {
      if (this.normalizedMaxCount && this.remainingUploadCount <= 0) {
        this.handleExceed()
        return false
      }
      if (!this.ratioParts) return true
      return this.getImageSize(file).then(
        ({ width, height }) => {
          const [ratioWidth, ratioHeight] = this.ratioParts
          if (width * ratioHeight !== height * ratioWidth) {
            this.$message.warning(`图片宽高比必须为${this.aspectRatio}，当前图片为${width}:${height}`)
            return Promise.reject(new Error('图片宽高比不符合要求'))
          }
          return file
        },
        error => {
          this.$message.error(error.message || '无法读取图片尺寸')
          return Promise.reject(error)
        }
      )
    },
    getUploadImageUrl(res) {
      return res && res.code === 200 ? (res.data.address || res.data.url || res.data.storage_path || '') : ''
    },
    hasExistingUrl(url) {
      return this.existingUrls.includes(url)
    },
    handleUploadSuccess(res) {
      const url = this.getUploadImageUrl(res)
      if (!url) {
        this.$message.error(res.message || res.msg || '上传失败')
        return
      }
      if (this.hasExistingUrl(url)) {
        this.$message.warning('不能上传重复的图片')
        return
      }
      this.$emit('select', [url])
      this.$message.success('上传成功')
      this.handleClose()
    },
    handleUploadError(error) {
      this.$message.error(error && error.message ? error.message : '上传失败')
    },
    async handleDirectUpload(options) {
      const uploadSession = this.uploadSession
      try {
        const file = options.file
        if (!file || !file.type || !file.type.startsWith('image/')) {
          throw new Error('请选择图片文件')
        }
        const data = await uploadDirectOss(file, {
          purpose: 'merchant_image',
          headers: this.headers,
          onProgress: event => options.onProgress && options.onProgress(event),
        })
        const url = data.url
        if (this.hasExistingUrl(url)) {
          throw new Error('不能上传重复的图片')
        }
        if (uploadSession !== this.uploadSession || !this.visible) return
        options.onSuccess && options.onSuccess({ code: 200, data })
      } catch (error) {
        if (uploadSession !== this.uploadSession || !this.visible) return
        options.onError && options.onError(error)
      }
    },
    confirmUrl() {
      const urls = this.urlText
        .split(/\n|,|，/)
        .map(item => item.trim())
        .filter(Boolean)

      if (!urls.length) {
        this.$message.warning('请填写图片URL')
        return
      }
      const invalid = urls.find(url => !/^https?:\/\/.+/i.test(url))
      if (invalid) {
        this.$message.warning('图片URL仅支持http或https')
        return
      }
      const seen = new Set()
      const duplicate = urls.find(url => {
        if (this.hasExistingUrl(url) || seen.has(url)) return true
        seen.add(url)
        return false
      })
      if (duplicate) {
        this.$message.warning('不能上传重复的图片')
        return
      }
      if (this.normalizedMaxCount && urls.length > this.remainingUploadCount) {
        this.handleExceed()
        return
      }

      this.$emit('select', this.multiple ? urls : [urls[0]])
      this.handleClose()
    },
    handleClose() {
      this.uploadSession += 1
      this.clearUploadFiles()
      this.$emit('update:visible', false)
    },
    clearUploadFiles() {
      const upload = this.$refs.imageUpload
      if (upload && typeof upload.clearFiles === 'function') upload.clearFiles()
    },
  },
}
</script>

<style scoped>
.image-source-upload {
  display: flex;
  justify-content: center;
}

.url-tips {
  color: #909399;
  font-size: 12px;
  margin-top: 8px;
}
</style>
